import { inject, Injectable } from "@angular/core";
import { Observable, tap, map } from "rxjs";

import { createStore, getRegistry, Store, withProps, } from "@ngneat/elf";
import { setEntities, EntitiesRef, selectEntities, updateEntities, withEntities, addEntities, deleteEntities } from '@ngneat/elf-entities';
import { persistState, localStorageStrategy } from "@ngneat/elf-persist-state";

import { SchemaService } from "../services/schema.service";
import { initializeRequestElf } from "../store/state";
import { selectLoading, withLoading } from "../store/stateprops";
import { Schema, SchemaInfo } from "../models/schema.model";
import { DateTime } from "luxon";
import { HasFogPService } from "../store/hasFogpStore";

@Injectable({ providedIn: "root" })
export class SchemaRepository {
    private readonly schemaService = inject(SchemaService);
    private readonly hasFogpService = inject(HasFogPService)

    private clinicStore = this.createClinicStore();
    private patientActiveSchemaStore = this.createPatientActiveSchemaStore();
    private patientHistoricalSchemaStore = this.createPatientHistoricalSchemaStore();
    private patientOtherSchemaStore = this.createPatientOtherSchemaStore();
    private patientOtherCompletedSchemaStore = this.createPatientOtherCompletedSchemaStore();
    private schemaInfoStore = this.createschemaInfoStore();

    private schemaStores: Store[] = [
        this.clinicStore,
        this.patientActiveSchemaStore,
        this.patientHistoricalSchemaStore,
        this.patientOtherSchemaStore,
        this.patientOtherCompletedSchemaStore,
        this.schemaInfoStore
    ];

    clinicSchemas$ = this.clinicStore.pipe(
        selectEntities(),
        map(entities => Object.values(entities).reverse()),
    );

    patientActiveSchemas$ = this.patientActiveSchemaStore.pipe(
        selectEntities(),
        map(entities => Object.values(entities).reverse())
    );

    patientHistoricalSchemas$ = this.patientHistoricalSchemaStore.pipe(
        selectEntities(),
        map(entities => Object.values(entities).reverse()),
    );

    patientOtherSchemas$ = this.patientOtherSchemaStore.pipe(
        selectEntities(),
        map(entities => Object.values(entities).reverse())
    );

    patientOtherCompletedSchemas$ = this.patientOtherCompletedSchemaStore.pipe(
        selectEntities(),
        map(entities => Object.values(entities).reverse())
    );

    schemaInfo$ = this.schemaInfoStore.pipe(selectEntities());

    clinicSchemasIsLoading$: Observable<boolean> = this.clinicStore.pipe(selectLoading());
    patientActiveSchemasIsLoading$: Observable<boolean> = this.clinicStore.pipe(selectLoading());
    patientHistoricalSchemasIsLoading$: Observable<boolean> = this.clinicStore.pipe(selectLoading());
    patientOtherSchemasIsLoading$: Observable<boolean> = this.clinicStore.pipe(selectLoading());
    patientOtherCompletedSchemasIsLoading$: Observable<boolean> = this.clinicStore.pipe(selectLoading());
    schemaInfoIsLoading$: Observable<boolean> = this.schemaInfoStore.pipe(selectLoading());

    private createClinicStore(): typeof store {
        const store = createStore(
            { name: "clinicSchemaStore" },
            withEntities<Schema, "schemaId">({ idKey: 'schemaId' }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

    private createPatientActiveSchemaStore(): typeof store {
        const store = createStore(
            { name: "patientActiveSchemaStore" },
            withEntities<Schema, "schemaId">({ idKey: 'schemaId' }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

    private createPatientHistoricalSchemaStore(): typeof store {
        const store = createStore(
            { name: "patientHistoricalSchemaStore" },
            withEntities<Schema, "schemaId">({ idKey: 'schemaId' }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

    private createPatientOtherSchemaStore(): typeof store {
        const store = createStore(
            { name: "patientOtherSchemaStore" },
            withEntities<Schema, "schemaId">({ idKey: 'schemaId' }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

    private createPatientOtherCompletedSchemaStore(): typeof store {
        const store = createStore(
            { name: "patientOtherSchemaStore" },
            withEntities<Schema, "schemaId">({ idKey: 'schemaId' }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

    private createschemaInfoStore(): typeof store {
        const store = createStore(
            { name: "schemaInfoStore" },
            withEntities<SchemaInfo, "schemaId">({ idKey: 'schemaId' }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

    private addSchemas(store: Store, schemas: any[], entiryRef?: EntitiesRef<string, string, string>) {
        store.update(
            setEntities(schemas, { ref: entiryRef }),
        );
    }

    getClinicSchemas$(): Observable<Schema[]> {
        return this.schemaService.postSchemasByUserId().pipe(
            initializeRequestElf(this.clinicStore),
            tap({
                next: (schemas: Schema[]) => {
                    this.addSchemas(this.clinicStore, schemas);
                },
                error: (error) => {
                    console.error('Error fetching schemas:', error);
                },
                complete: () => {
                    console.log('Fetch schemas complete');
                }
            })
        )
    }

    getPatientActiveSchemas$(): Observable<Schema[]> {
        return this.schemaService.postPatientActiveSchemas().pipe(
            initializeRequestElf(this.patientActiveSchemaStore),
            tap({
                next: (schemas: Schema[]) => {
                    this.addSchemas(this.patientActiveSchemaStore, schemas);
                },
                error: (error) => {
                    console.error('Error fetching schemas:', error);
                },
                complete: () => {
                    console.log('Fetch schemas complete');
                }
            })
        )
    }

    getPatientHistoricalSchemas$(): Observable<Schema[]> {
        return this.schemaService.postPatientHistoricalSchemas().pipe(
            initializeRequestElf(this.patientHistoricalSchemaStore),
            tap({
                next: (schemas: Schema[]) => {
                    this.addSchemas(this.patientHistoricalSchemaStore, schemas);
                },
                error: (error) => {
                    console.error('Error fetching schemas:', error);
                },
                complete: () => {
                    console.log('Fetch schemas complete');
                }
            })
        )
    }

    getPatientOtherSchemas$(): Observable<Schema[]> {
        return this.schemaService.postPatientOtherSchemas().pipe(
            initializeRequestElf(this.patientOtherSchemaStore),
            tap({
                next: (schemas: Schema[]) => {
                    this.addSchemas(this.patientOtherSchemaStore, schemas);
                },
                error: (error) => {
                    console.error('Error fetching schemas:', error);
                },
                complete: () => {
                    console.log('Fetch schemas complete');
                }
            })
        )
    }

    getPatientOtherCompletedSchemas$(): Observable<Schema[]> {
        return this.schemaService.postPatientOtherCompletedSchemas().pipe(
            initializeRequestElf(this.patientOtherCompletedSchemaStore),
            tap({
                next: (schemas: Schema[]) => {
                    this.addSchemas(this.patientOtherCompletedSchemaStore, schemas);
                },
                error: (error) => {
                    console.error('Error fetching schemas:', error);
                },
                complete: () => {
                    console.log('Fetch schemas complete');
                }
            })
        )
    }

    getSchemaInfo$(schemaId: number): Observable<SchemaInfo> {
        const schemas: SchemaInfo[] = [];
        return this.schemaService.getSchemaInfoById(schemaId).pipe(
            initializeRequestElf(this.schemaInfoStore),
            tap({
                next: (schema: SchemaInfo) => {
                    schemas.push(schema)
                    this.addSchemas(this.schemaInfoStore, schemas);
                },
                error: (error) => {
                    console.error('Error fetching schema:', error);
                },
                complete: () => {
                    console.log('Fetch schemas complete');
                }
            })
        )
    }

    getHasFogPForms$(): Observable<boolean> {
        return this.schemaService.getHasFogPForms().pipe(
            tap({
                next: (result) => {
                    this.hasFogpService.setHasForms(result);
                }
            })
        )
    }

    updateSchemaChangedDate(schemaId: number): void {
        this.schemaStores.forEach(store => {
            if (store.getValue().ids.includes(schemaId)) {
                store.update(
                    updateEntities(schemaId, { changedDate: DateTime.now().toJSDate() }),
                );
            }
        });
    }

    updateSchemaStoreByRequisition(schemaId: number): void {
        this.schemaService.getSchemaById(schemaId).subscribe({
            next: (schema: Schema) => {
                this.clinicStore.update(
                    addEntities(schema)
                );
            }
        });
    }

    updateSchemaStoreByDelete(schemaId: number): void {
        this.schemaStores.forEach(store => {
            if (store.getValue().ids.includes(schemaId)) {
                store.update(
                    deleteEntities(schemaId)
                );
            }
        });
    }

    resetStores() {
        getRegistry().forEach((store) => store.reset());
    }

    get getClinicStore() {
        return this.clinicStore.getValue();
    }

    get getPatientActiveSchemaStore() {
        return this.patientActiveSchemaStore.getValue();
    }

    get getPatientHistoricalSchemaStore() {
        return this.patientActiveSchemaStore.getValue();
    }

    get getPatientOtherSchemaStore() {
        return this.patientActiveSchemaStore.getValue();
    }

    get getschemaInfoStore() {
        return this.schemaInfoStore.getValue();
    }

}
